// src/api/index.ts
import axios from "axios";

// Determine if running on localhost / development environment
export const isLocalhost = (): boolean => {
  if (typeof window === "undefined") {
    return process.env.NODE_ENV === "development";
  }
  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    hostname.endsWith(".local")
  );
};

export const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (isLocalhost()
    ? "http://localhost:4500/api"
    : "https://api.dieurlaubsberater.com/api");

export const API_ROOT_URL =
  process.env.REACT_APP_API_ROOT_URL ||
  (isLocalhost()
    ? "http://localhost:4500"
    : "https://api.dieurlaubsberater.com");

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding token & ensuring dynamic baseURL
api.interceptors.request.use(
  (config) => {
    config.baseURL =
      process.env.REACT_APP_API_URL ||
      (isLocalhost()
        ? "http://localhost:4500/api"
        : "https://api.dieurlaubsberater.com/api");
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login if not on login page and not a login request
    const isLoginRequest = error.config?.url?.includes("/auth/login");
    const isOnLoginPage = window.location.pathname === "/login";

    if (error.response?.status === 401 && !isLoginRequest && !isOnLoginPage) {
      localStorage.removeItem("adminToken");
      window.location.href = "/login";
    }

    // Always return the error so components can handle it
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email: string, password: string) =>
    api.post("/auth/login", { email, password }),
  logout: () => api.post("/auth/logout"),
  getCurrentUser: () => api.get("/auth/me"),
};

export const bookingsApi = {
  getAll: (params?: any) => api.get("/bookings", { params }),
  getById: (id: string) => api.get(`/bookings/${id}`),
  create: (data: any) => api.post("/bookings", data),
  update: (id: string, data: any) => api.put(`/bookings/${id}`, data),
  delete: (id: string) => api.delete(`/bookings/${id}`),
  updateStatus: (id: string, status: string) =>
    api.patch(`/bookings/${id}/status`, { status }),
  getStats: () => api.get("/bookings/stats"),
  sendToGoodJourney: (id: string) =>
    api.post(`/bookings/${id}/send-goodjourney`),
};

export const toursApi = {
  getAll: (params?: any) => api.get("/tours", { params }),
  getById: (id: string) => api.get(`/tours/${id}`),
  create: (data: any) => api.post("/tours", data),
  update: (id: string, data: any) => api.put(`/tours/${id}`, data),
  delete: (id: string) => api.delete(`/tours/${id}`),
};

export default api;
